import { useState, useCallback, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  api,
  AssetLocation,
  AssetLocationTypes,
  KeyMangerBarcodeResultType,
  KeyMovementStatus,
  KeyMovementInfo,
  ShiftCategory,
  TaskCategory,
  type ShiftTask,
  type IKeyManagerBarcodeScanKeyMovementResult,
} from "@/logic/api";
import { SessionContext } from "@/utility/useSession";
import { usePreferences } from "@/utility/usePreferences";
import { useDate } from "@/utility/useDate";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { useGetUserByStaffPass } from "@/pages/driverShiftTracker/hooks/useGetUserByStaffPass";
import { useGetShiftData } from "@/pages/driverShiftTracker/hooks/useGetShiftData";
import { useAddShiftEvent } from "@/hooks/useAddShiftEvent";
import { useGetShiftTasks } from "@/hooks/useGetShiftTasks";
import { useSoundEffects, SoundType } from "@/utility/utils";

const SUCCESS_DISPLAY_MS = 4_000;
const ERROR_DISPLAY_MS = 4_000;
const STAFF_PASS_PATTERN = /^USR-[a-zA-Z0-9]+$/i;

interface ScannedUser {
  uuid: string;
  name: string;
  shiftUuid: string | null;
}

type TaskAssignmentOutcome = "taskUpdated" | "taskUpdatedAndOnShift" | "taskUpdatedAndOnShiftAtCarPark";

const isAssignmentTask = (task: ShiftTask): boolean =>
  task.category === TaskCategory.Driving || task.category === TaskCategory.NonDriving;

type UserScanLocation = Pick<AssetLocation, "id">;

type RetainedTarget =
  | { kind: "location"; location: AssetLocation }
  | { kind: "user"; user: ScannedUser; location: UserScanLocation | null };

export type ScanningState =
  | { status: "idle" }
  | { status: "location_selected"; location: AssetLocation }
  | { status: "user_selected"; user: ScannedUser; location: UserScanLocation | null }
  | { status: "key_scanned"; lastScannedKeyBarcode: string }
  | { status: "task_selected"; task: ShiftTask }
  | { status: "processing" }
  | { status: "success"; message: string; warning?: string }
  | { status: "error"; message: string; detail?: string };

export interface UseScanningFlowResult {
  state: ScanningState;
  tasks: ShiftTask[];
  expectKeyScanBeforeLocation: boolean;
  handleScan: (value: string) => void;
  handleLocationPicked: (location: AssetLocation) => void;
  handleTaskSelected: (taskKey: string) => void;
  reset: () => void;
}

export const useScanningFlow = (): UseScanningFlowResult => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const { localizeDate } = useDate();
  const playSound = useSoundEffects();
  const { keymovements_key_then_location, taskAssignmentMarksOnShiftEnabled } = usePreferences();
  const isScanningStaffPassDriverLocationReleased = useReleaseManagement(ReleaseFlag.releaseScanningStaffPassDriverLocation);
  const isKeyThenLocationScanningReleased = useReleaseManagement(ReleaseFlag.releaseKeyThenLocationScanning);
  const isMultiAssetLocationMoveReleased = useReleaseManagement(ReleaseFlag.releaseMultiAssetLocationMove);
  const isTaskAssignmentMarksOnShiftReleased = useReleaseManagement(ReleaseFlag.releaseTaskAssignmentMarksOnShift);
  const isCarParkSpecificReleased = useReleaseManagement(ReleaseFlag.releaseDriverAvailabilityCarSpecific);
  const expectKeyScanBeforeLocation = isKeyThenLocationScanningReleased && keymovements_key_then_location;
  const canAutoStartShiftOnAssignment = isTaskAssignmentMarksOnShiftReleased && taskAssignmentMarksOnShiftEnabled;

  const [state, setState] = useState<ScanningState>({ status: "idle" });
  const busyRef = useRef(false);
  const retainedTargetRef = useRef<RetainedTarget | null>(null);

  const getUserByStaffPass = useGetUserByStaffPass();
  const addShiftEvent = useAddShiftEvent();
  const { data: tasksData } = useGetShiftTasks();
  const tasks = tasksData ?? [];
  // TODO: DD - useGetShiftData fetches shift data for every driver on the tenant; consider scoping this to the scanned driver only.
  const { data: shiftData } = useGetShiftData();
  const carParkUuid = session.current?.site?.home?.uuid;
  const carParkName = session.current?.site?.home?.name ?? "";
  const queryClient = useQueryClient();
  const operatorUserUuid = session.current?.site?.user?.uuid;
  const tenantId = session.current?.site?.tenant?.id;

  const resolveAssignmentOutcome = useCallback((user: ScannedUser): TaskAssignmentOutcome => {
    if (user.shiftUuid || !canAutoStartShiftOnAssignment) {
      return "taskUpdated";
    }

    // TODO: DD - if shiftData hasn't resolved yet (still loading/inflight, or errored), we can't tell
    // whether the driver has an open shift at another car park, so we fall back to "taskUpdatedAndOnShift".
    // Not a blocker, but worth revisiting if this proves to give the wrong message in practice.
    const existingShift = shiftData?.data.find((shift) => shift.user.uuid === user.uuid);
    const hadOpenShiftAtOtherCarPark = Boolean(
      isCarParkSpecificReleased &&
      existingShift?.shiftUuid &&
      existingShift.period.startDate &&
      existingShift.carPark?.uuid != null &&
      existingShift.carPark.uuid !== carParkUuid
    );

    return hadOpenShiftAtOtherCarPark ? "taskUpdatedAndOnShiftAtCarPark" : "taskUpdatedAndOnShift";
  }, [canAutoStartShiftOnAssignment, shiftData, isCarParkSpecificReleased, carParkUuid]);

  const fetchScanData = api.hooks.keyManager.barcodeScanKeyMovement();
  // had to do this so that we don't create a new reference on each render and add this dependency everywhere
  const fetchScanDataRef = useRef(fetchScanData);
  fetchScanDataRef.current = fetchScanData;

  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetToIdle = useCallback(() => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    busyRef.current = false;
    retainedTargetRef.current = null;
    setState({ status: "idle" });
  }, []);

  // Keep showing the retained target (destination or driver) so the operator can scan straight
  // into the next asset/key
  const resumeRetainedTarget = useCallback(() => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    busyRef.current = false;

    const target = isMultiAssetLocationMoveReleased ? retainedTargetRef.current : null;
    if (target?.kind === "location") {
      setState({ status: "location_selected", location: target.location });
    } else if (target?.kind === "user") {
      setState({ status: "user_selected", user: target.user, location: target.location });
    } else {
      setState({ status: "idle" });
    }
  }, [isMultiAssetLocationMoveReleased]);

  const reset = useCallback(() => resetToIdle(), [resetToIdle]);

  const showSuccessThenReset = useCallback((message: string, warning?: string) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);

    retainedTargetRef.current = null;
    setState({ status: "success", message, warning });
    playSound(warning ? SoundType.Warning : SoundType.Good);
    feedbackTimerRef.current = setTimeout(resetToIdle, SUCCESS_DISPLAY_MS);
  }, [resetToIdle, playSound]);

  const showMovementSuccess = useCallback(({ message, warning, location }: { message: string; warning?: string; location: AssetLocation }) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);

    retainedTargetRef.current = isMultiAssetLocationMoveReleased ? { kind: "location", location } : null;
    setState({ status: "success", message, warning });
    playSound(warning ? SoundType.Warning : SoundType.Good);
    feedbackTimerRef.current = setTimeout(resumeRetainedTarget, SUCCESS_DISPLAY_MS);
  }, [resumeRetainedTarget, playSound, isMultiAssetLocationMoveReleased]);

  const showUserMovementSuccess = useCallback(({ message, warning, user, location }: { message: string; warning?: string; user: ScannedUser; location: UserScanLocation | null }) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);

    retainedTargetRef.current = isMultiAssetLocationMoveReleased ? { kind: "user", user, location } : null;
    setState({ status: "success", message, warning });
    playSound(warning ? SoundType.Warning : SoundType.Good);
    feedbackTimerRef.current = setTimeout(resumeRetainedTarget, SUCCESS_DISPLAY_MS);
  }, [resumeRetainedTarget, playSound, isMultiAssetLocationMoveReleased]);

  const showErrorThenReset = useCallback((message: string, detail?: string) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    // Clear immediately so a scan during the still-visible error window can't move to a stale target
    retainedTargetRef.current = null;
    setState({ status: "error", message, detail });
    playSound(SoundType.Bad);
    feedbackTimerRef.current = setTimeout(resetToIdle, ERROR_DISPLAY_MS);
  }, [resetToIdle, playSound]);

  // If a key scan fails while a driver is selected, that's a problem with the key, not the
  // driver, so keep the driver selected instead of clearing everything back to idle
  const showErrorThenResumeUser = useCallback((message: string, user: ScannedUser, location: UserScanLocation | null, detail?: string) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    retainedTargetRef.current = isMultiAssetLocationMoveReleased ? { kind: "user", user, location } : null;
    setState({ status: "error", message, detail });
    playSound(SoundType.Bad);
    feedbackTimerRef.current = setTimeout(resumeRetainedTarget, ERROR_DISPLAY_MS);
  }, [resumeRetainedTarget, playSound, isMultiAssetLocationMoveReleased]);

  // Selecting or scanning a new location replaces the retained destination
  const selectLocation = useCallback((location: AssetLocation) => {
    retainedTargetRef.current = isMultiAssetLocationMoveReleased ? { kind: "location", location } : null;
    setState({ status: "location_selected", location });
    playSound(SoundType.Swap);
  }, [isMultiAssetLocationMoveReleased, playSound]);

  // We need to reset the busy state and clear the feedback timer on unmount
  useEffect(() => {
    return () => {
      busyRef.current = false;
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const handleStaffPassScan = useCallback(async (value: string) => {
    if (!STAFF_PASS_PATTERN.test(value)) {
      showErrorThenReset(t("scanning.error.invalidStaffPass"));
      return;
    }
  if (feedbackTimerRef.current) {
    clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = null;
  }
  retainedTargetRef.current = null;
    setState({ status: "processing" });
    busyRef.current = true;

    try {
      const user = await getUserByStaffPass.mutateAsync({ staffPass: value });
      const scannedUser: ScannedUser = {
        uuid: user.uuid,
        name: `${user.firstName} ${user.lastName}`,
        shiftUuid: user.currentShift?.uuid ?? null,
      };
      setState({
        status: "user_selected",
        user: scannedUser,
        location: isScanningStaffPassDriverLocationReleased ? (user.location ?? null) : null,
      });
      playSound(SoundType.Swap);
    } catch {
      showErrorThenReset(t("scanning.error.staffPassNotFound"));
    } finally {
      busyRef.current = false;
    }
  }, [getUserByStaffPass, isScanningStaffPassDriverLocationReleased, t, showErrorThenReset, playSound]);

  const processTaskAction = useCallback(async (task: ShiftTask, user: ScannedUser): Promise<TaskAssignmentOutcome> => {
    const titleLower = task.title.toLowerCase();
    const isShiftAction = titleLower.includes("shift");
    const isBreakAction = titleLower.includes("break");
    const createdByUuid = session?.current?.site?.user?.uuid;
    if (!createdByUuid) {
      throw new Error(t("scanning.error.scanFailed"));
    }
    const now = new Date().toISOString();

    switch (task.category) {
      case TaskCategory.On:
        if (isShiftAction) {
          await addShiftEvent.mutateAsync({
            userUuid: user.uuid,
            createdByUuid,
            category: ShiftCategory.Shift,
            action: TaskCategory.On,
            occurredAt: now,
          });
        } else if (isBreakAction) {
          const shiftUuid = user.shiftUuid;
          if (!shiftUuid) throw new Error(t("scanning.error.noActiveShift"));
          await addShiftEvent.mutateAsync({
            userUuid: user.uuid,
            shiftUuid,
            createdByUuid,
            category: ShiftCategory.Break,
            action: TaskCategory.On,
            occurredAt: now,
          });
        } else {
          throw new Error(t("scanning.error.scanFailed"));
        }
        break;

      case TaskCategory.Off:
        if (isShiftAction) {
          await addShiftEvent.mutateAsync({
            userUuid: user.uuid,
            createdByUuid,
            category: ShiftCategory.Shift,
            action: TaskCategory.Off,
            occurredAt: now,
          });
        } else if (isBreakAction) {
          const shiftUuid = user.shiftUuid;
          if (!shiftUuid) throw new Error(t("scanning.error.noActiveShift"));
          await addShiftEvent.mutateAsync({
            userUuid: user.uuid,
            shiftUuid,
            createdByUuid,
            category: ShiftCategory.Break,
            action: TaskCategory.Off,
            occurredAt: now,
          });
        } else {
          throw new Error(t("scanning.error.scanFailed"));
        }
        break;

      case TaskCategory.Driving:
      case TaskCategory.NonDriving:
      default: {
        const shiftUuid = user.shiftUuid;
        if (!shiftUuid && !canAutoStartShiftOnAssignment) {
          throw new Error(t("scanning.error.noActiveShift"));
        }
        const outcome = resolveAssignmentOutcome(user);
        await addShiftEvent.mutateAsync({
          userUuid: user.uuid,
          shiftUuid: shiftUuid ?? undefined,
          createdByUuid,
          category: ShiftCategory.Assignment,
          action: task.key,
          occurredAt: now,
        });
        if (!shiftUuid && canAutoStartShiftOnAssignment) {
          // The backend auto-started a new shift for this driver; useAddShiftEvent doesn't update the shift data cache, so we need to invalidate it here to ensure the Driver Availability page shows the driver as on-shift.
          await queryClient.invalidateQueries({ queryKey: ["shift", "data", operatorUserUuid, tenantId] });
        }
        return outcome;
      }
    }
    return "taskUpdated";
  }, [addShiftEvent, session, t, canAutoStartShiftOnAssignment, resolveAssignmentOutcome, queryClient, operatorUserUuid, tenantId]);

  const handleTaskActionForUser = useCallback(async (taskKey: string, user: ScannedUser) => {
    const task = tasks.find(tk => tk.key === taskKey);
    if (!task) {
      showErrorThenReset(t("scanning.error.unknownTaskCode"));
      return;
    }

    const isStartShiftTask = task.category === TaskCategory.On && task.title.toLowerCase().includes("shift");
    const canSkipShiftCheck = isAssignmentTask(task) && canAutoStartShiftOnAssignment;
    if (!isStartShiftTask && !canSkipShiftCheck && !user.shiftUuid) {
      showErrorThenReset(t("scanning.error.noActiveShift"));
      return;
    }

    setState({ status: "processing" });
    busyRef.current = true;

    try {
      const outcome = await processTaskAction(task, user);
      showSuccessThenReset(
        t(`scanning.feedback.${outcome}`, { task: task.title, name: user.name, carPark: carParkName })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : t("scanning.error.scanFailed");
      showErrorThenReset(message);
    } finally {
      busyRef.current = false;
    }
  }, [tasks, processTaskAction, showSuccessThenReset, showErrorThenReset, t, canAutoStartShiftOnAssignment, carParkName]);

  const handleTaskSelectedFirst = useCallback((taskKey: string) => {
    const task = tasks.find(tk => tk.key === taskKey);
    if (!task) {
      showErrorThenReset(t("scanning.error.unknownTaskCode"));
      return;
    }
    setState({ status: "task_selected", task });
    playSound(SoundType.Swap);
  }, [tasks, showErrorThenReset, playSound, t]);

  const performTaskForStaffPass = useCallback(async (task: ShiftTask, staffPass: string) => {
    setState({ status: "processing" });
    busyRef.current = true;

    try {
      const user = await getUserByStaffPass.mutateAsync({ staffPass });
      const scannedUser: ScannedUser = {
        uuid: user.uuid,
        name: `${user.firstName} ${user.lastName}`,
        shiftUuid: user.currentShift?.uuid ?? null,
      };

      const isStartShiftTask = task.category === TaskCategory.On && task.title.toLowerCase().includes("shift");
      const canSkipShiftCheck = isAssignmentTask(task) && canAutoStartShiftOnAssignment;
      if (!isStartShiftTask && !canSkipShiftCheck && !scannedUser.shiftUuid) {
        busyRef.current = false;
        showErrorThenReset(t("scanning.error.noActiveShift"));
        return;
      }

      const outcome = await processTaskAction(task, scannedUser);
      showSuccessThenReset(
        t(`scanning.feedback.${outcome}`, { task: task.title, name: scannedUser.name, carPark: carParkName })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : t("scanning.error.scanFailed");
      showErrorThenReset(message);
    } finally {
      busyRef.current = false;
    }
  }, [getUserByStaffPass, processTaskAction, showSuccessThenReset, showErrorThenReset, t, canAutoStartShiftOnAssignment, carParkName]);

  const handleBarcodeScanForLocation = useCallback((
    result: IKeyManagerBarcodeScanKeyMovementResult,
    location: AssetLocation
  ) => {
    if (result.type === KeyMangerBarcodeResultType.err) {
      if (result.err) {
        showErrorThenReset(t(result.err));
      } else {
        showErrorThenReset(t("scanning.error.scanFailed"));
      }
      return;
    }

    if (result.type === KeyMangerBarcodeResultType.location) {
      //we scanned another location, replace the current one
      if (result.location) {
        selectLocation(result.location);
      }
      return;
    }

    if (result.type === KeyMangerBarcodeResultType.booking) {
      if (!result.booking) {
        showErrorThenReset(t("errors.keyBarcodeNotFound"));
        return;
      }

      // Handle movement restrictions
      if (result.keyMovementInfo) {
        if (result.keyMovementStatus === KeyMovementStatus.block) {
          let detail = "";
          if (result.keyMovementInfo === KeyMovementInfo.wrongDate) {
            detail = t("errors.keyMovementBlockedDate").toUpperCase();
          } else if (result.keyMovementInfo === KeyMovementInfo.wrongTimeSlot) {
            detail = t("errors.keyMovementBlockedDateTimeslot").toUpperCase();
          } else if (result.keyMovementInfo === KeyMovementInfo.differentAssignedLane || result.keyMovementInfo === KeyMovementInfo.noAssignedLane) {
            detail = t("errors.keyMovementBlockedAllocation").toUpperCase();
          } else if (result.keyMovementInfo === KeyMovementInfo.zoneNotAllowed) {
            detail = t("errors.keyMovementBlockedZone").toUpperCase();
          }
          showErrorThenReset(
            t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }),
            detail
          );
          return;
        }

        if (result.keyMovementStatus === KeyMovementStatus.alert) {
          showMovementSuccess({
            message: t("messages.keyMoved", { ref: result.booking.ref, location: location.name }),
            warning: t("messages.keyMovementWarning"),
            location,
          });
          return;
        }

        // Unknown movement status with info present, treat that as an error instead of doingabsolutely nothing
        showErrorThenReset(t("scanning.error.scanFailed"));
        return;
      }

      // Successful move — retain the destination so more assets can be moved to it
      if (result.keyMoved && result.vehicleMoved) {
        showMovementSuccess({ message: t("scanning.feedback.keyAndVehicleMoved", { ref: result.booking.ref, location: location.name }), location });
      } else if (result.vehicleMoved) {
        showMovementSuccess({ message: t("scanning.feedback.vehicleMoved", { ref: result.booking.ref, location: location.name }), location });
      } else {
        showMovementSuccess({ message: t("scanning.feedback.keyMoved", { ref: result.booking.ref, location: location.name }), location });
      }
    }
  }, [showMovementSuccess, showErrorThenReset, selectLocation, t]);

  const sendBarcodeScan = useCallback((
    scan: string,
    locationId: number | null,
    onResult: (result: IKeyManagerBarcodeScanKeyMovementResult | null) => void,
    lookupOnly = false
  ) => {
    fetchScanDataRef.current.send(
      session.current.context,
      {
        scan,
        locationId,
        scanDate: localizeDate(new Date()).toISO() ?? "",
        lookupOnly,
      },
      onResult
    );
  }, [session, localizeDate]);

  // Handles a scan while a driver is selected, whether that's the user_selected state itself
  // or a fast follow-up scan for a driver still retained after a success screen.
  const handleScanForUser = useCallback((trimmed: string, user: ScannedUser, location: UserScanLocation | null) => {
    // Check if it's a task key
    const task = tasks.find(tk => tk.key === trimmed);
    if (task) {
      handleTaskActionForUser(trimmed, user);
      return;
    }

    // Else we send to backend (could be a booking ref for key assignment to user)
    // Clear the pending "resume retained target" timer so it can't fire mid-request and
    // reopen the door to a second, overlapping scan while this one is still in flight
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    setState({ status: "processing" });
    busyRef.current = true;

    sendBarcodeScan(trimmed, location?.id ?? null, (result) => {
      busyRef.current = false;
      if (!result) {
        showErrorThenResumeUser(t("scanning.error.scanFailed"), user, location);
        return;
      }

      if (result.type === KeyMangerBarcodeResultType.err) {
        showErrorThenResumeUser(result.err ? t(result.err) : t("scanning.error.scanFailed"), user, location);
        return;
      }

      if (result.type === KeyMangerBarcodeResultType.location && result.location) {
        //scanned a location while user was selected, we switch to location context
        selectLocation(result.location);
        return;
      }

      if (result.type === KeyMangerBarcodeResultType.booking) {
        if (!result.booking) {
          showErrorThenResumeUser(t("errors.keyBarcodeNotFound"), user, location);
          return;
        }
        // Key assigned to user — retain the driver so more keys can be scanned to them
        showUserMovementSuccess({
          message: t("scanning.feedback.keyMoved", { ref: result.booking.ref, location: user.name }),
          user,
          location,
        });
        return;
      }

      showErrorThenResumeUser(t("scanning.error.scanFailed"), user, location);
    });
  }, [tasks, handleTaskActionForUser, sendBarcodeScan, showErrorThenResumeUser, selectLocation, showUserMovementSuccess, t]);

  const handleScan = useCallback((value: string) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;

    // Ignore scans while we are processing
    if (busyRef.current || state.status === "processing") {
      playSound(SoundType.Busy);
      return;
    }

    // First scan, classify as staff pass or send to barcodeScanKeyMovement
    if (state.status === "idle" || state.status === "success" || state.status === "error") {
      if (STAFF_PASS_PATTERN.test(trimmed)) {
        handleStaffPassScan(trimmed);
        return;
      }

      const retainedTarget = isMultiAssetLocationMoveReleased ? retainedTargetRef.current : null;

      // A retained driver performs a real move, taking precedence just like a retained location
      if (retainedTarget?.kind === "user") {
        handleScanForUser(trimmed, retainedTarget.user, retainedTarget.location);
        return;
      }

      const retainedLocation = retainedTarget?.kind === "location" ? retainedTarget.location : null;

      // Not a staff pass, we send to backend to classify as a location or booking
      // Clear the pending "resume retained target" timer so it can't fire mid-request and
      // reopen the door to a second, overlapping scan while this one is still in flight
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
      setState({ status: "processing" });
      busyRef.current = true;

      // A retained destination performs a real move, so it takes precedence over the key-first lookup
      const isKeyFirstScan = expectKeyScanBeforeLocation && !retainedLocation;
      sendBarcodeScan(trimmed, retainedLocation?.id ?? null, (result) => {
        busyRef.current = false;
        if (!result) {
          showErrorThenReset(t("scanning.error.scanFailed"));
          return;
        }

        if (result.type === KeyMangerBarcodeResultType.err) {
          showErrorThenReset(result.err ? t(result.err) : t("scanning.error.scanFailed"));
          return;
        }

        if (result.type === KeyMangerBarcodeResultType.location && result.location) {
          selectLocation(result.location);
          return;
        }

        if (result.type === KeyMangerBarcodeResultType.booking && isKeyFirstScan) {
          setState({ status: "key_scanned", lastScannedKeyBarcode: trimmed });
          playSound(SoundType.Swap);
          return;
        }

        // Booking scanned with a retained destination: the backend has moved it there
        if (result.type === KeyMangerBarcodeResultType.booking && retainedLocation) {
          handleBarcodeScanForLocation(result, retainedLocation);
          return;
        }

        // Booking scanned first without location/user. we throw an error (when not key-first)
        showErrorThenReset(t("scanning.error.scanLocationOrUserFirst"));
      }, isKeyFirstScan /* lookupOnly: true for key first flow to get booking without move */);
      return;
    }

    // Waiting for location after key scanned (key-first flow)
    if (state.status === "key_scanned") {
      const { lastScannedKeyBarcode } = state;

      if (STAFF_PASS_PATTERN.test(trimmed)) {
        handleStaffPassScan(trimmed);
        return;
      }

      setState({ status: "processing" });
      busyRef.current = true;

      sendBarcodeScan(trimmed, null, (result) => {
        if (result?.type === KeyMangerBarcodeResultType.location && result.location) {
          const location = result.location;
          sendBarcodeScan(lastScannedKeyBarcode, location.id, (moveResult) => {
            busyRef.current = false;
            if (!moveResult) {
              showErrorThenReset(t("scanning.error.scanFailed"));
              return;
            }
            handleBarcodeScanForLocation(moveResult, location);
          });
        } else {
          busyRef.current = false;
          if (!result) {
            showErrorThenReset(t("scanning.error.scanFailed"));
            return;
          }
          if (result.type === KeyMangerBarcodeResultType.err) {
            showErrorThenReset(result.err ? t(result.err) : t("errors.scanLocation"));
          } else {
            showErrorThenReset(t("errors.scanLocation"));
          }
        }
      });
      return;
    }

    // Task selected, waiting for staff pass (task-first flow)
    if (state.status === "task_selected") {
      const { task } = state;

      if (STAFF_PASS_PATTERN.test(trimmed)) {
        performTaskForStaffPass(task, trimmed);
        return;
      }

      const scannedTask = tasks.find(tk => tk.key === trimmed);
      if (scannedTask) {
        handleTaskSelectedFirst(trimmed);
        return;
      }

      showErrorThenReset(t("scanning.error.scanStaffPassToApply"));
      return;
    }

    // Second scan with location selected
    if (state.status === "location_selected") {
      const { location } = state;

      // Staff pass replaces location with user context
      if (STAFF_PASS_PATTERN.test(trimmed)) {
        handleStaffPassScan(trimmed);
        return;
      }

      setState({ status: "processing" });
      busyRef.current = true;

      sendBarcodeScan(trimmed, location.id, (result) => {
        busyRef.current = false;
        if (!result) {
          showErrorThenReset(t("scanning.error.scanFailed"));
          return;
        }
        handleBarcodeScanForLocation(result, location);
      });
      return;
    }

    //Second scan with user selected
    if (state.status === "user_selected") {
      const { user, location } = state;

      // Another staff pass replaces current user
      if (STAFF_PASS_PATTERN.test(trimmed)) {
        handleStaffPassScan(trimmed);
        return;
      }

      handleScanForUser(trimmed, user, location);
      return;
    }
  }, [
    state, tasks, playSound, handleStaffPassScan, handleBarcodeScanForLocation,
    handleScanForUser, handleTaskSelectedFirst, performTaskForStaffPass,
    sendBarcodeScan, showErrorThenReset, t,
    expectKeyScanBeforeLocation, isMultiAssetLocationMoveReleased, selectLocation,
  ]);

  const handleLocationPicked = useCallback(async (location: AssetLocation) => {
    if (busyRef.current || state.status === "processing") {
      playSound(SoundType.Busy);
      return;
    }

    if (state.status === "key_scanned") {
      const { lastScannedKeyBarcode } = state;
      setState({ status: "processing" });
      busyRef.current = true;
      sendBarcodeScan(lastScannedKeyBarcode, location.id, (result) => {
        busyRef.current = false;
        if (!result) {
          showErrorThenReset(t("scanning.error.scanFailed"));
          return;
        }
        handleBarcodeScanForLocation(result, location);
      });
      return;
    }

    if (state.status === "task_selected") {
      if (location.type === AssetLocationTypes.User && STAFF_PASS_PATTERN.test(location.code)) {
        await performTaskForStaffPass(state.task, location.code);
      } else {
        showErrorThenReset(t("scanning.error.scanStaffPassToApply"));
      }
      return;
    }

    if (location.type === AssetLocationTypes.User && STAFF_PASS_PATTERN.test(location.code)) {
      // User location, resolve to user for task actions
      setState({ status: "processing" });
      busyRef.current = true;
      try {
        const user = await getUserByStaffPass.mutateAsync({ staffPass: location.code });
        setState({
          status: "user_selected",
          user: {
            uuid: user.uuid,
            name: `${user.firstName} ${user.lastName}`,
            shiftUuid: user.currentShift?.uuid ?? null,
          },
          location,
        });
        playSound(SoundType.Swap);
      } catch {
        showErrorThenReset(t("scanning.error.staffPassNotFound"));
      } finally {
        busyRef.current = false;
      }
    } else {
      selectLocation(location);
    }
  }, [state, playSound, getUserByStaffPass, showErrorThenReset, sendBarcodeScan, handleBarcodeScanForLocation, performTaskForStaffPass, selectLocation, t]);

  const handleTaskSelected = useCallback((taskKey: string) => {
    if (expectKeyScanBeforeLocation && (state.status === "idle" || state.status === "success" || state.status === "error")) {
      handleTaskSelectedFirst(taskKey);
      return;
    }
    if (state.status !== "user_selected") {
      showErrorThenReset(t("scanning.error.scanLocationOrUserFirst"));
      return;
    }
    handleTaskActionForUser(taskKey, state.user);
  }, [state, expectKeyScanBeforeLocation, handleTaskSelectedFirst, handleTaskActionForUser, showErrorThenReset, t]);

  return {
    state,
    tasks,
    expectKeyScanBeforeLocation,
    handleScan,
    handleLocationPicked,
    handleTaskSelected,
    reset,
  };
};
